// PhonePePaymentStatus.jsx

import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Spin } from "antd";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import API from "../../utils/api";

const PhonePePaymentStatus = () => {
  const [searchParams] = useSearchParams();
  const [redirectCountdown, setRedirectCountdown] = useState(5);
  const merchantOrderId = searchParams.get("merchantOrderId");

  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("Checking payment status...");
  const [paymentData, setPaymentData] = useState(null);
  console.log(paymentData);

  const celebrationTriggered = useRef(false);

  useEffect(() => {
    if (status !== "success") return;

    setRedirectCountdown(5);

    const interval = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          window.location.href = "/wallet";
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [status]);

  useEffect(() => {
    if (!merchantOrderId) {
      setStatus("failed");
      setMessage("Invalid payment request.");
      return;
    }

    const checkPayment = async () => {
      try {
        const { data } = await API.get(
          `/wallet/phonepe-payment-status?merchantOrderId=${encodeURIComponent(
            merchantOrderId,
          )}`,
        );

        setPaymentData(data);

        if (data.status === "COMPLETED") {
          setStatus("success");
          setMessage("Payment successful!");

          if (!celebrationTriggered.current) {
            celebrationTriggered.current = true;

            setTimeout(() => {
              confetti({
                particleCount: 120,
                spread: 90,
                startVelocity: 45,
                origin: {
                  x: 0.5,
                  y: 0.65,
                },
              });

              setTimeout(() => {
                confetti({
                  particleCount: 70,
                  spread: 100,
                  startVelocity: 40,
                  origin: {
                    x: 0.08,
                    y: 0.7,
                  },
                });

                confetti({
                  particleCount: 70,
                  spread: 100,
                  startVelocity: 40,
                  origin: {
                    x: 0.92,
                    y: 0.7,
                  },
                });
              }, 250);

              setTimeout(() => {
                confetti({
                  particleCount: 80,
                  spread: 120,
                  startVelocity: 35,
                  origin: {
                    x: 0.5,
                    y: 0.35,
                  },
                });
              }, 550);
            }, 300);
          }
        } else if (data.status === "FAILED") {
          setStatus("failed");
          setMessage("Payment failed.");
        } else {
          setStatus("pending");
          setMessage("Payment is still processing...");
        }
      } catch (error) {
        console.error("Payment status error:", error);

        setStatus("failed");
        setMessage("Unable to verify payment status.");
      }
    };

    checkPayment();
  }, [merchantOrderId]);

  const goToWallet = () => {
    window.location.href = "/wallet";
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gray-100 p-4">
      {/* =========================
          SUCCESS BACKGROUND
      ========================== */}

      {status === "success" && (
        <>
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.4,
            }}
            animate={{
              opacity: 1,
              scale: 1.5,
            }}
            transition={{
              duration: 1.2,
              ease: "easeOut",
            }}
            className="pointer-events-none absolute left-1/2 top-1/2 h-[550px] w-[550px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-400/20 blur-[110px]"
          />

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.8,
            }}
            animate={{
              opacity: [0, 0.5, 0.2, 0.5],
              scale: [0.9, 1.05, 0.95, 1.05],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="pointer-events-none absolute left-1/2 top-1/2 h-[400px] w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-full border border-green-300/30"
          />
        </>
      )}

      {/* =========================
          MAIN CARD
      ========================== */}

      <motion.div
        initial={{
          opacity: 0,
          scale: 0.9,
          y: 25,
        }}
        animate={{
          opacity: 1,
          scale: 1,
          y: 0,
        }}
        transition={{
          duration: 0.5,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="relative z-10 w-full max-w-md overflow-hidden rounded-3xl bg-white p-8 text-center shadow-2xl"
      >
        {/* =========================
            CHECKING
        ========================== */}

        {status === "checking" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Spin size="large" />

            <h2 className="mt-5 text-xl font-bold text-gray-900">
              Checking Payment
            </h2>

            <p className="mt-2 text-gray-500">{message}</p>
          </motion.div>
        )}

        {/* =========================
            SUCCESS
        ========================== */}

        {status === "success" && (
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.85,
              y: 30,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              y: 0,
            }}
            transition={{
              duration: 0.6,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            {/* Floating celebration */}

            <motion.div
              animate={{
                y: [-8, 8, -8],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="pointer-events-none absolute left-8 top-8 text-xl"
            >
              ✨
            </motion.div>

            <motion.div
              animate={{
                y: [8, -8, 8],
                rotate: [0, -10, 10, 0],
              }}
              transition={{
                duration: 3.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="pointer-events-none absolute right-8 top-12 text-xl"
            >
              🎉
            </motion.div>

            {/* Success icon */}

            <div className="relative mx-auto mb-6 flex h-28 w-28 items-center justify-center">
              <motion.div
                initial={{
                  scale: 0.7,
                  opacity: 0,
                }}
                animate={{
                  scale: [0.9, 1.15, 1],
                  opacity: [0, 0.35, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeOut",
                }}
                className="absolute inset-0 rounded-full border-2 border-green-400"
              />

              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 180,
                  damping: 12,
                }}
                className="absolute inset-2 rounded-full bg-gradient-to-br from-green-400 to-emerald-600 p-1 shadow-lg shadow-green-300/50"
              >
                <div className="flex h-full w-full items-center justify-center rounded-full bg-white">
                  <svg width="55" height="55" viewBox="0 0 55 55" fill="none">
                    <motion.path
                      d="M15 28L23 36L41 18"
                      stroke="currentColor"
                      strokeWidth="5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-600"
                      initial={{
                        pathLength: 0,
                        opacity: 0,
                      }}
                      animate={{
                        pathLength: 1,
                        opacity: 1,
                      }}
                      transition={{
                        delay: 0.25,
                        duration: 0.55,
                        ease: "easeOut",
                      }}
                    />
                  </svg>
                </div>
              </motion.div>
            </div>

            {/* Heading */}

            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.35,
              }}
            >
              <div className="mb-1 text-xs font-bold uppercase tracking-[0.2em] text-green-600">
                Payment Completed
              </div>

              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900">
                Payment Successful
              </h2>

              <p className="mt-2 text-gray-500">
                Your wallet has been recharged successfully.
              </p>
            </motion.div>

            {/* Amount */}

            <motion.div
              initial={{
                opacity: 0,
                scale: 0.95,
                y: 15,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                y: 0,
              }}
              transition={{
                delay: 0.5,
              }}
              className="mt-7 rounded-2xl border border-green-100 bg-gradient-to-br from-green-50 to-emerald-50 p-5"
            >
              <p className="text-xs font-medium uppercase tracking-wider text-gray-500">
                Amount Added
              </p>

              <motion.p
                initial={{
                  scale: 0.8,
                }}
                animate={{
                  scale: 1,
                }}
                transition={{
                  delay: 0.6,
                  type: "spring",
                  stiffness: 180,
                }}
                className="mt-1 text-4xl font-extrabold text-green-600"
              >
                ₹
                {Number(paymentData?.amount || 0).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </motion.p>

              <div className="mt-3 flex items-center justify-center gap-2 text-sm font-medium text-green-700">
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-200">
                  ✓
                </span>
                Added to your wallet
              </div>
            </motion.div>

            {/* Transaction details */}

            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.65,
              }}
              className="mt-4 rounded-xl border border-gray-100 bg-gray-50 p-4 text-left"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-500">Payment Method</span>

                <span className="font-semibold text-gray-800">PhonePe</span>
              </div>

              <div className="my-3 border-t border-gray-200" />

              <div className="flex items-center justify-between gap-4">
                <span className="text-sm text-gray-500">Order ID</span>

                <span
                  className="max-w-[190px] truncate text-xs font-medium text-gray-700"
                  title={merchantOrderId}
                >
                  {merchantOrderId}
                </span>
              </div>

              <div className="my-3 border-t border-gray-200" />

              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Status</span>

                <span className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
                  <span className="h-2 w-2 rounded-full bg-green-500" />
                  Completed
                </span>
              </div>
            </motion.div>

            {/* Wallet button */}

            <motion.button
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                delay: 0.8,
              }}
              whileHover={{
                scale: 1.02,
                boxShadow: "0 15px 35px rgba(124, 58, 237, 0.25)",
              }}
              whileTap={{
                scale: 0.97,
              }}
              onClick={goToWallet}
              className="mt-6 w-full rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 py-3.5 font-semibold text-white shadow-lg shadow-purple-200 transition-all"
            >
              Go to Wallet
            </motion.button>

            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95 }}
              className="mt-4 space-y-1"
            >
              <p className="text-xs text-gray-400">
                Redirecting to your wallet in{" "}
                <span className="font-bold text-purple-600">
                  {redirectCountdown}s
                </span>
              </p>

              <p className="text-xs text-gray-400">
                Thank you for your payment ❤️
              </p>
            </motion.div>
          </motion.div>
        )}

        {/* =========================
            FAILED
        ========================== */}

        {status === "failed" && (
          <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            animate={{
              opacity: 1,
              scale: 1,
            }}
          >
            <motion.div
              initial={{
                scale: 0,
              }}
              animate={{
                scale: 1,
              }}
              transition={{
                type: "spring",
                stiffness: 220,
                damping: 12,
              }}
              className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-red-100"
            >
              <motion.span
                initial={{
                  scale: 0,
                  rotate: -45,
                }}
                animate={{
                  scale: 1,
                  rotate: 0,
                }}
                transition={{
                  delay: 0.15,
                  type: "spring",
                }}
                className="text-5xl text-red-500"
              >
                ✕
              </motion.span>
            </motion.div>

            <h2 className="mt-6 text-2xl font-bold text-red-600">
              Payment Failed
            </h2>

            <p className="mt-2 text-gray-500">{message}</p>

            <motion.button
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.96 }}
              onClick={goToWallet}
              className="mt-6 rounded-lg bg-gray-700 px-6 py-2 text-white transition hover:bg-gray-800"
            >
              Back to Wallet
            </motion.button>
          </motion.div>
        )}

        {/* =========================
            PENDING
        ========================== */}

        {status === "pending" && (
          <motion.div
            initial={{
              opacity: 0,
            }}
            animate={{
              opacity: 1,
            }}
          >
            <motion.div
              animate={{
                rotate: 360,
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "linear",
              }}
              className="mb-4 text-6xl"
            >
              ⏳
            </motion.div>

            <h2 className="text-2xl font-bold text-yellow-600">
              Payment Processing
            </h2>

            <p className="mt-2 text-gray-500">
              Please wait while we confirm your payment.
            </p>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PhonePePaymentStatus;
